import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

// Business Suite access gate. Renders the real module only for Business, Partner,
// or Admin accounts (and respects the Features Control toggle for Business/Partner;
// Admins always get through). Everyone else sees a "not available / upgrade" screen.
// Visibility/access gating only — the gated modules themselves are unchanged.
function ModuleUnavailable({ toggledOff }) {
  const navigate = useNavigate();
  return (
    <div className="max-w-xl mx-auto text-center py-20 px-6" data-testid="module-unavailable">
      <div className="w-14 h-14 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-5">
        <Lock className="w-6 h-6 text-brand" />
      </div>
      <h1 className="font-serif text-3xl text-ink mb-2">Not available on your plan</h1>
      <p className="text-muted-foreground mb-7">
        {toggledOff
          ? "This module is currently turned off for your account. Please contact your administrator."
          : "This is a Two Degrees Business Suite feature, available on the Business and Brand Partner plans. Upgrade to unlock Projects, Docs, Calendar, HR and more."}
      </p>
      <div className="flex items-center justify-center gap-3">
        <button data-testid="module-unavailable-back" onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm text-ink hover:border-brand transition-colors">
          <ArrowLeft className="w-4 h-4" />Back home
        </button>
        {!toggledOff && (
          <button data-testid="module-unavailable-upgrade" onClick={() => navigate("/membership")}
            className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm hover:opacity-90 transition-opacity">
            See plans
          </button>
        )}
      </div>
    </div>
  );
}

export default function BusinessGate({ featureKey, allowUserKey, children }) {
  const { isBusinessSuite, isAdmin, features, user } = useAuth();
  const extraAllow = !!(allowUserKey && user && user[allowUserKey]);
  const allowed = isBusinessSuite || extraAllow;
  if (!allowed) return <ModuleUnavailable />;
  // Business/Partner must respect a Features Control toggle-off; Admins always pass.
  if (!isAdmin && !extraAllow && featureKey && features && features[featureKey] === false) {
    return <ModuleUnavailable toggledOff />;
  }
  return children;
}
